import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Check, Loader2, MessageCircle, CalendarDays } from "lucide-react";
import Reveal from "@/components/Reveal";
import GoldSeam from "@/components/GoldSeam";
import { api } from "@/lib/api";
import { PACKAGES, GOOGLE_CALENDAR_URL, waLink, WHATSAPP_DISPLAY } from "@/lib/site";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const inputCls =
  "w-full bg-transparent border-b border-[rgba(169,129,47,0.26)] focus:border-gold py-3 text-ink placeholder:text-inkdim/50 outline-none transition-colors duration-300 text-[15px]";
const labelCls = "block text-[10px] uppercase tracking-[0.3em] text-gold mb-1";

export default function Booking() {
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [activeDay, setActiveDay] = useState(0);
  const [form, setForm] = useState({ name: "", email: "", pkg: "session_60", slot: "", note: "" });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");

  useEffect(() => {
    api
      .get("/booking/availability")
      .then(({ data }) => setDays(data.days || []))
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, []);

  const slots = useMemo(() => days[activeDay]?.slots ?? [], [days, activeDay]);
  const update = (patch) => {
    setForm((f) => ({ ...f, ...patch }));
    setErrors((e) => {
      const next = { ...e };
      Object.keys(patch).forEach((k) => delete next[k === "pkg" ? "package" : k]);
      return next;
    });
  };
  const set = (k) => (e) => update({ [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.name.trim()) errs.name = "Your name, so Rajdeep knows who's arriving.";
    if (!EMAIL_RE.test(form.email)) errs.email = "A valid email for the confirmation.";
    if (!form.slot) errs.slot = "Pick a time that suits you.";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setStatus("sending");
    try {
      const { data } = await api.post("/booking/checkout", {
        name: form.name,
        email: form.email,
        package: form.pkg,
        slot_start: form.slot,
        note: form.note,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch {
      setStatus("error");
    }
  };

  return (
    <main className="pt-20" data-testid="booking-page">
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div
          className="absolute -top-32 right-0 w-[460px] h-[460px] rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(169,129,47,0.13), transparent 62%)" }}
          aria-hidden="true"
        />
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <p className="eyebrow mb-6">Book a session</p>
            <h1
              className="font-display text-ink font-medium leading-[1.06] max-w-3xl"
              style={{ fontSize: "clamp(2.4rem, 5vw, 4rem)" }}
              data-testid="booking-heading"
            >
              Pick a time. The rest is a conversation.
            </h1>
            <p className="mt-8 text-inkdim leading-relaxed max-w-2xl text-[15px] md:text-base">
              Sessions run Monday to Friday, 10:00 to 18:00 IST — sixty minutes each, with a
              quarter of an hour between them so nothing feels rushed. Payment confirms the slot.
            </p>
            <GoldSeam variant="horizontal" className="mt-10 w-56 h-10 opacity-70" delay={0.2} />
          </Reveal>
        </div>
      </section>

      <section className="pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-14">
          <form onSubmit={submit} noValidate className="lg:col-span-7 space-y-12" data-testid="booking-form">
            <div>
              <p className="text-[11px] uppercase tracking-[0.24em] text-gold mb-6">01 — Choose the work</p>
              <div className="space-y-3">
                {PACKAGES.map((p) => (
                  <button
                    type="button"
                    key={p.id}
                    onClick={() => update({ pkg: p.id })}
                    data-testid={`booking-package-${p.id}`}
                    className={`w-full flex items-baseline justify-between gap-4 border px-6 py-5 text-left transition-colors duration-300 ${
                      form.pkg === p.id
                        ? "border-gold bg-sand/60"
                        : "border-[rgba(169,129,47,0.26)] hover:border-gold"
                    }`}
                  >
                    <span>
                      <span className="block font-display text-xl md:text-2xl text-ink">{p.label}</span>
                      <span className="block mt-1 text-xs text-inkdim">{p.note}</span>
                    </span>
                    <span className="font-display text-2xl text-gold shrink-0">{p.price}</span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="text-[11px] uppercase tracking-[0.24em] text-gold mb-6">02 — Choose a time (IST)</p>
              {loading && (
                <p className="flex items-center gap-2 text-sm text-inkdim" data-testid="booking-loading">
                  <Loader2 size={15} className="animate-spin" /> Fetching open slots…
                </p>
              )}
              {loadError && (
                <p className="text-sm text-[#9E3B1F]" data-testid="booking-availability-error">
                  Couldn't load the calendar just now. Please refresh, or message on WhatsApp.
                </p>
              )}
              {!loading && !loadError && (
                <>
                  <div className="flex gap-2 overflow-x-auto pb-3" data-testid="booking-day-tabs">
                    {days.map((d, i) => (
                      <button
                        type="button"
                        key={d.date}
                        onClick={() => setActiveDay(i)}
                        data-testid={`booking-day-${d.date}`}
                        className={`shrink-0 border px-4 py-3 text-[11px] uppercase tracking-[0.18em] transition-colors duration-300 ${
                          activeDay === i
                            ? "border-gold bg-deep text-canvas"
                            : "border-[rgba(169,129,47,0.26)] text-inkdim hover:border-gold"
                        }`}
                      >
                        {d.label}
                      </button>
                    ))}
                  </div>
                  <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="booking-slots-grid">
                    {slots.map((s) => (
                      <button
                        type="button"
                        key={s.start}
                        disabled={!s.available}
                        onClick={() => update({ slot: s.start })}
                        data-testid={`booking-slot-${s.start}`}
                        className={`border px-4 py-4 text-sm transition-colors duration-300 ${
                          form.slot === s.start
                            ? "border-gold bg-deep text-canvas"
                            : s.available
                              ? "border-[rgba(169,129,47,0.26)] text-ink hover:border-gold"
                              : "border-[rgba(169,129,47,0.14)] text-inkdim/40 line-through cursor-not-allowed"
                        }`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
              {errors.slot && (
                <p className="text-xs text-[#9E3B1F] mt-3" data-testid="booking-slot-error">{errors.slot}</p>
              )}
            </div>

            <div>
              <p className="text-[11px] uppercase tracking-[0.24em] text-gold mb-6">03 — Who's arriving</p>
              <div className="grid sm:grid-cols-2 gap-8">
                <div>
                  <label htmlFor="bk-name" className={labelCls}>Name</label>
                  <input
                    id="bk-name"
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Your name"
                    className={inputCls}
                    data-testid="booking-name-input"
                  />
                  {errors.name && <p className="text-xs text-[#9E3B1F] mt-2" data-testid="booking-name-error">{errors.name}</p>}
                </div>
                <div>
                  <label htmlFor="bk-email" className={labelCls}>Email</label>
                  <input
                    id="bk-email"
                    type="email"
                    value={form.email}
                    onChange={set("email")}
                    placeholder="you@example.com"
                    className={inputCls}
                    data-testid="booking-email-input"
                  />
                  {errors.email && <p className="text-xs text-[#9E3B1F] mt-2" data-testid="booking-email-error">{errors.email}</p>}
                </div>
              </div>
              <div className="mt-8">
                <label htmlFor="bk-note" className={labelCls}>Anything Rajdeep should know (optional)</label>
                <textarea
                  id="bk-note"
                  rows={3}
                  value={form.note}
                  onChange={set("note")}
                  placeholder="The loop you keep finding yourself in."
                  className={`${inputCls} resize-none`}
                  data-testid="booking-note-textarea"
                />
              </div>
            </div>

            {status === "error" && (
              <p className="text-sm text-[#9E3B1F]" data-testid="booking-error-message">
                The checkout didn't open. Nothing was charged — please try once more.
              </p>
            )}

            <motion.button
              type="submit"
              disabled={status === "sending"}
              whileTap={{ scale: 0.98 }}
              className="bg-deep text-canvas text-xs uppercase tracking-[0.24em] font-semibold px-10 py-4 hover:bg-gold transition-colors duration-300 disabled:opacity-60 disabled:cursor-wait"
              data-testid="booking-submit-button"
            >
              {status === "sending" ? "Opening secure checkout…" : "Continue to payment"}
            </motion.button>
          </form>

          <aside className="lg:col-span-5">
            <div className="border border-[rgba(169,129,47,0.26)] bg-paper p-8" data-testid="booking-aside">
              <p className="eyebrow mb-6">What happens next</p>
              <ul className="space-y-4 text-sm text-inkdim leading-relaxed">
                {[
                  "Payment is taken securely by Stripe — card details never touch this site.",
                  "The slot is held the moment you reach checkout and confirmed once payment clears.",
                  "You'll get a confirmation email; Rajdeep sends the private joining link before the session.",
                  "Need to move it? Reply to the confirmation at least 24 hours ahead — one free reschedule.",
                ].map((line) => (
                  <li key={line} className="flex gap-3">
                    <Check size={15} strokeWidth={1.6} className="text-gold mt-0.5 shrink-0" />
                    {line}
                  </li>
                ))}
              </ul>

              <div className="mt-10 pt-8 border-t border-[rgba(169,129,47,0.26)]">
                <p className="eyebrow mb-5">Prefer another way?</p>
                <a
                  href={GOOGLE_CALENDAR_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="booking-google-calendar-link"
                  className="inline-flex items-center gap-2 text-sm text-ink gold-underline-link"
                >
                  <CalendarDays size={15} strokeWidth={1.6} className="text-gold" />
                  Pick a slot on Rajdeep's live calendar
                </a>
                <a
                  href={waLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="booking-whatsapp-link"
                  className="mt-4 inline-flex items-center gap-2 text-sm text-ink gold-underline-link"
                >
                  <MessageCircle size={15} strokeWidth={1.6} className="text-gold" />
                  WhatsApp {WHATSAPP_DISPLAY}
                </a>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </main>
  );
}
